import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { addDays, differenceInCalendarDays } from "date-fns";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import SearchFilters from "@/components/SearchFilters";
import MapView from "@/components/Map";
import placeholder from "@/assets/placeholder.png?url";
import { SEOHead } from "@/components/SEOHead";
import ParkingSpotCard from "@/components/ParkingSpotCard";
import { SlidersHorizontal } from "lucide-react";

// availability helpers du allerede har
import { expandAvailability, dayHasAvailability, type Interval } from "@/lib/availability";

type Listing = {
  id: string;
  title: string;
  address: string;
  size: "S" | "M" | "L" | "XL";
  photos: string[] | null;
  base_price_day: number | null;
  geo_lat: number | null;
  geo_lng: number | null;
  visibility: boolean;
  facilities: {
    charger?: boolean;
    covered?: boolean;
    camera?: boolean;
  } | null;
};

type AvailabilityRow = {
  id: string;
  listing_id: string;
  type: "single" | "range" | "recurring";
  start_at: string;
  end_at: string;
  rrule: string | null;
};

const SIZE_LABELS: Record<Listing["size"], string> = {
  S: "Lille (< 4.2 m)",
  M: "Medium (4.2–4.8 m)",
  L: "Stor (4.8–5.2 m)",
  XL: "Ekstra stor (> 5.2 m)",
};

function daterangeHasAvailability(from: Date, to: Date, windows: Interval[]) {
  const days = Math.max(0, differenceInCalendarDays(to, from)) + 1;
  for (let i = 0; i < days; i++) {
    const d = addDays(from, i);
    if (!dayHasAvailability(d, windows)) return false;
  }
  return true;
}

export default function Search() {
  const [sp] = useSearchParams();
  const q = (sp.get("query") || "").trim();
  const fromStr = sp.get("from") || "";
  const toStr = sp.get("to") || "";
  
  // Filters
  const minPrice = parseInt(sp.get("minPrice") || "0");
  const maxPrice = parseInt(sp.get("maxPrice") || "500");
  const sizes = sp.get("sizes")?.split(",").filter(Boolean) || [];
  const hasCharger = sp.get("charger") === "true";
  const hasCovered = sp.get("covered") === "true";
  const hasCamera = sp.get("camera") === "true";

  const rangeStart = useMemo(() => (fromStr ? new Date(fromStr + "T00:00:00") : null), [fromStr]);
  const rangeEnd   = useMemo(() => (toStr   ? new Date(toStr   + "T23:59:59") : null), [toStr]);

  // Kandidatlister (tekst/adresse) – kun synlige og godkendte
  const listingsQ = useQuery({
    queryKey: ["search-listings", q],
    queryFn: async (): Promise<Listing[]> => {
      let query = supabase
        .from("listings")
        .select("id,title,address,size,photos,base_price_day,geo_lat,geo_lng,visibility,facilities")
        .eq("visibility", true)
        .eq("approval_status", "approved")
        .limit(100);

      if (q) {
        query = query.or(`address.ilike.%${q}%,title.ilike.%${q}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as Listing[];
    },
  });

  // Availability for de fundne listings (.in)
  const availabilityQ = useQuery({
    enabled: !!listingsQ.data?.length,
    queryKey: ["search-availability", (listingsQ.data || []).map((l) => l.id)],
    queryFn: async () => {
      const ids = (listingsQ.data || []).map((l) => l.id);
      if (!ids.length) return new Map();

      const { data, error } = await supabase
        .from("availability")
        .select("*")
        .in("listing_id", ids);

      if (error) throw error;

      const map = new Map();
      ids.forEach((id) => map.set(id, []));
      (data || []).forEach((row) => {
        const arr = map.get(row.listing_id) || [];
        arr.push(row as AvailabilityRow);
        map.set(row.listing_id, arr);
      });
      return map;
    },
  });

  const filtered = useMemo(() => {
    let listings = listingsQ.data || [];
    
    // Apply price filter
    if (minPrice > 0 || maxPrice < 500) {
      listings = listings.filter((l) => {
        const priceKr = Math.round((l.base_price_day || 0) / 100);
        return priceKr >= minPrice && priceKr <= maxPrice;
      });
    }
    
    // Apply size filter
    if (sizes.length > 0) {
      listings = listings.filter((l) => sizes.includes(l.size));
    }
    
    // Apply facility filters
    if (hasCharger || hasCovered || hasCamera) {
      listings = listings.filter((l) => {
        const facilities = l.facilities || {};
        if (hasCharger && !facilities.charger) return false;
        if (hasCovered && !facilities.covered) return false;
        if (hasCamera && !facilities.camera) return false;
        return true;
      });
    }
    
    // Apply date filter if dates are selected
    if (rangeStart && rangeEnd) {
      const avMap = availabilityQ.data;
      if (!avMap) return [];
      
      listings = listings.filter((l) => {
        const rows = avMap.get(l.id) || [];
        const windows: Interval[] = expandAvailability(rows as any, rangeStart, rangeEnd);
        return daterangeHasAvailability(rangeStart, rangeEnd, windows);
      });
    } else {
      // No dates selected - still filter out listings without any future availability
      const avMap = availabilityQ.data;
      if (avMap) {
        const now = new Date();
        const futureEnd = addDays(now, 365);
        
        listings = listings.filter((l) => {
          const rows = avMap.get(l.id) || [];
          if (rows.length === 0) return false; // No availability rows
          const windows: Interval[] = expandAvailability(rows as any, now, futureEnd);
          return windows.length > 0; // Only show if there are future windows
        });
      }
    }
    
    return listings;
  }, [listingsQ.data, availabilityQ.data, rangeStart, rangeEnd, minPrice, maxPrice, sizes, hasCharger, hasCovered, hasCamera]);

  const loading = listingsQ.isLoading || availabilityQ.isLoading;

  const hasActiveFilters = 
    minPrice > 0 || 
    maxPrice < 500 || 
    sizes.length > 0 || 
    hasCharger || 
    hasCovered || 
    hasCamera;

  const clearFilters = () => {
    const params = new URLSearchParams();
    if (q) params.set("query", q);
    if (fromStr) params.set("from", fromStr);
    if (toStr) params.set("to", toStr);
    window.location.href = `/search?${params.toString()}`;
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead 
        title={q ? `Parkering nær ${q} | SharePark` : "Søg ledig privat parkering | SharePark"}
        description={`Find ledige private parkeringspladser${q ? ` nær ${q}` : " i Danmark"}. Sammenlign pris, beliggenhed og datoer på SharePark.`}
        keywords={`parkering ${q}, privat parkering, ledig parkering, lej parkeringsplads, parkeringsplads danmark`}
        url={q ? `https://sharepark.dk/search?query=${encodeURIComponent(q)}` : "https://sharepark.dk/search"}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "Hvordan finder jeg en ledig parkeringsplads?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Brug søgningen til at vælge område, datoer, pris og faciliteter. SharePark viser kun synlige og godkendte pladser med fremtidig tilgængelighed.",
              },
            },
            {
              "@type": "Question",
              name: "Bliver datoerne reserveret efter betaling?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Ja, når betalingen er gennemført og verificeret, oprettes bookingen, så de valgte datoer ikke fremstår som frie for andre lejere.",
              },
            },
            {
              "@type": "Question",
              name: "Hvorfor kan jeg ikke se husnummeret i søgeresultaterne?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "SharePark viser kun den maskede adresse før booking for at beskytte værtens privatliv. Den fulde adresse deles først efter en bekræftet booking.",
              },
            },
          ],
        }}
      />
      <Header />
      <main className="container mx-auto px-4 py-6 max-w-7xl">
        {/* Mobile Filters Button */}
        <div className="md:hidden mb-4">
          <SearchFilters isMobile />
        </div>

        <div className="mb-6 rounded-lg border bg-card p-5 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
          <h1 className="text-2xl font-bold">Søgeresultater</h1>
          <div className="mt-1 text-sm text-muted-foreground">
            {q ? <>Omkring <span className="font-medium text-foreground">{q}</span></> : "Hele Danmark"}
            {rangeStart && rangeEnd ? <> · {fromStr} → {toStr}</> : " · vælg dato for præcis tilgængelighed"}
          </div>
          </div>
          {!loading && (
            <div className="rounded-full bg-secondary px-3 py-1 text-sm font-medium text-secondary-foreground">
              {filtered.length} {filtered.length === 1 ? "resultat" : "resultater"}
            </div>
          )}
        </div>

        <div className="mt-4">
          {hasActiveFilters && (
            <div className="flex flex-wrap gap-2 items-center">
              <span className="inline-flex items-center gap-1 text-sm font-medium"><SlidersHorizontal className="h-4 w-4" /> Aktive filtre:</span>
              {(minPrice > 0 || maxPrice < 500) && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm">
                  Pris: {minPrice}-{maxPrice} kr
                </span>
              )}
              {sizes.map((size) => (
                <span key={size} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm">
                  Størrelse: {SIZE_LABELS[size as keyof typeof SIZE_LABELS]}
                </span>
              ))}
              {hasCharger && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm">
                  El-lader
                </span>
              )}
              {hasCovered && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm">
                  Overdækket
                </span>
              )}
              {hasCamera && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm">
                  Kamera
                </span>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-muted-foreground hover:text-foreground"
              >
                Nulstil alle
              </Button>
            </div>
          )}
        </div>
        </div>

        {/* Desktop Layout with Sidebar */}
        <div className="flex gap-6">
          {/* Filters Sidebar - Desktop only */}
          <aside className="hidden md:block w-64 flex-shrink-0">
            <SearchFilters />
          </aside>

          {/* Results */}
          <div className="flex-1">
            {loading && <div>Henter resultater…</div>}

            {!loading && filtered.length === 0 && (
              <div className="text-muted-foreground">Ingen resultater. Prøv at ændre søgning eller datoer.</div>
            )}

            {!loading && filtered.length > 0 && (
              <Tabs defaultValue="grid" className="w-full">
                <TabsList className="mb-5 grid w-full grid-cols-2 sm:w-auto sm:inline-grid">
                  <TabsTrigger value="grid">Liste visning</TabsTrigger>
                  <TabsTrigger value="map">Kort visning</TabsTrigger>
                </TabsList>
                
                <TabsContent value="grid">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {filtered.map((l) => {
                      const priceKr = Math.round((l.base_price_day || 0) / 100);

                      return (
                        <ParkingSpotCard
                          key={l.id}
                          id={l.id}
                          title={l.title}
                          address={l.address}
                          price={priceKr}
                          size={l.size}
                          hasCharger={Boolean(l.facilities?.charger)}
                          isCovered={Boolean(l.facilities?.covered)}
                          photos={l.photos?.length ? l.photos : [placeholder]}
                        />
                      );
                    })}
                  </div>
                </TabsContent>
                
                <TabsContent value="map">
                  <MapView 
                    listings={filtered.map(l => ({
                      id: l.id,
                      title: l.title,
                      address: l.address,
                      geo_lat: l.geo_lat,
                      geo_lng: l.geo_lng,
                      base_price_day: l.base_price_day,
                    }))}
                    height="600px"
                  />
                </TabsContent>
              </Tabs>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
